<template>
  <div class="autocomplete">
    <input
      :id="id"
      v-model="searchTerm"
      maxlength="100"
      type="search"
      autocomplete="off"
      @focus="onChange"
      @input="onChange"
      @blur="onSelect($event, UserEvents.blur)"
      @keydown.down="onArrowDown"
      @keydown.up="onArrowUp"
      @keydown.enter.prevent="onEnter"
      @keydown.esc.prevent="reset"
      @keydown.tab="reset"
    />
    <icon icon="search" title="Zoeken" />
    <ul v-show="isOpen" id="autocomplete-results">
      <li
        v-for="(result, index) in results"
        :key="result.slug"
        :class="{ 'is-active': index === arrowCounter }"
        @click="selectResult(result)"
      >
        {{ result }}
      </li>
    </ul>
  </div>
</template>

<script>
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import UserEvents from '@/helpers/enums/userEvents'

export default {
  components: {
    Icon,
  },
  props: {
    list: {
      type: Array,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    modelValue: {
      type: String,
      default: '',
    },
    minChars: {
      type: Number,
      default: 0,
    },
  },
  emits: [UserEvents.change, UserEvents.select, UserEvents.blur],
  data() {
    return {
      results: [],
      searchTerm: '',
      arrowCounter: 0,
      UserEvents,
    }
  },
  computed: {
    isOpen() {
      return this.results.length > 0
    },
  },

  watch: {
    modelValue() {
      this.searchTerm = this.modelValue
    },
    searchTerm() {
      this.$emit(UserEvents.change, this.searchTerm)
    },
  },

  mounted() {
    document.addEventListener(UserEvents.click, this.handleClickOutside)
    this.searchTerm = this.modelValue
  },
  unmounted() {
    document.removeEventListener(UserEvents.click, this.handleClickOutside)
  },

  methods: {
    onChange() {
      if (this.searchTerm.length >= this.minChars && this.list.length > -1) {
        this.results = this.list
          .filter((accessory) => {
            const term = this.searchTerm.toLowerCase()
            return accessory.toLowerCase().includes(term)
          })
          .slice(0, 10)
      } else {
        this.reset()
      }
    },
    onArrowDown() {
      if (this.arrowCounter < this.results.length - 1) {
        this.arrowCounter += 1
      } else {
        this.arrowCounter = 0
      }
    },
    onArrowUp() {
      if (this.arrowCounter > 0) {
        this.arrowCounter -= 1
      } else {
        this.arrowCounter = this.results.length - 1
      }
    },
    onEnter() {
      const result = this.results[this.arrowCounter]
      if (result) this.selectResult(result)
    },
    onSelect(e, eventName = UserEvents.select) {
      this.$emit(eventName, this.searchTerm, eventName === UserEvents.blur)
    },
    selectResult(result) {
      this.searchTerm = result
      this.onSelect()
      this.reset()
    },
    reset() {
      this.results = []
      this.arrowCounter = -1
    },
    handleClickOutside(evt) {
      if (!this.$el.contains(evt.target)) {
        this.reset()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
input {
  width: 100%;
}

.autocomplete {
  margin-bottom: 1em;
  position: relative;

  &:focus-within {
    input {
      border-color: $gray;
    }
    svg {
      color: $gray;
    }
  }
}

svg {
  position: absolute;
  right: 0.5em;
  top: 50%;
  margin-top: -12px;
  color: $color-border;
  transition: color 0.2s ease-in-out;
}

ul {
  @include list-reset();

  border: 2px solid $gray;
  border-top: 0;
  position: absolute;
  background: $white;
  top: 100%;
  left: 0;
  right: 0;
  z-index: z-index(autocomplete);
}

li {
  padding: 0.25em 0.625rem;
  cursor: pointer;

  &.is-active,
  &:hover {
    background-color: $color-primary;
    color: $white;
  }
}
</style>
